import React, {useEffect, useState} from 'react';
import {Drawer} from 'antd';
import {useTranslation} from 'react-i18next';
import {useQuery} from '@apollo/client';
import {useHashState} from 'utils/hashState';
import {PricingSchemeApplyFilterInitialValue} from './config/filterOptions';
import {PricingSchemeApplyColumns} from "./PricingSchemeApplyColumns";
import {usePricingSchemeApplyQuery} from "./hooks/usePricingSchemeApplyQuery";
import PricingSchemeApplyFilter from "./PricingSchemeApplyFilter";
import ApplySchemeComp from './ApplySchemeComp';
import TablePaginationBar from '../../../../../components/TablePaginationBar';
import {LIST_PRICING_SCHEME_APPLY_OPERATOR_OPTIONS, ListOperatorOptionsResponse} from './graphql/queries/listOperatorOptions';
interface PricingSchemeApplyTabProps {
    businessScenario: number | null;
}

const PricingSchemeApplyPage: React.FC<PricingSchemeApplyTabProps> = ({ businessScenario }) => {
    const {t} = useTranslation();

    const [tempFilters, setTempFilters] = useHashState(PricingSchemeApplyFilterInitialValue);
    const [filters, setFilters] = useHashState(PricingSchemeApplyFilterInitialValue);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    
    // 使用自定义hook获取数据
    const {
        pricingSchemeApplyData,
        loading,
        refetch,
        totalElements,
        buildQueryVariables
    } = usePricingSchemeApplyQuery(filters, businessScenario, currentPage, pageSize);

    const {
        data: operatorOptionsData,
        loading: operatorOptionsLoading,
        refetch: refetchOperatorOptions
    } = useQuery<ListOperatorOptionsResponse>(
        LIST_PRICING_SCHEME_APPLY_OPERATOR_OPTIONS,
        { variables: { businessScenario: businessScenario ?? undefined } }
    );
    const operatorOptions = operatorOptionsData?.listPricingSchemeApplyOperatorOptions || [];

    const handleResetFilters = () => {
        setTempFilters(PricingSchemeApplyFilterInitialValue);
        setFilters(PricingSchemeApplyFilterInitialValue);
    };

    const handleFilter = () => {
        setFilters(tempFilters);
        setCurrentPage(1);
    };

    // 添加useEffect监听状态变化后执行查询
    useEffect(() => {
        refetch(buildQueryVariables());
    }, [filters, currentPage, pageSize]);
    
    // 处理分页变化
    const handlePageChange = (page: number, pageSize: number) => {
        setCurrentPage(page);
        setPageSize(pageSize);
    };

    const [bindDrawerOpen, setBindDrawerOpen] = useState(false);
    const showBindDrawer = () => {
        setBindDrawerOpen(true);
    };
    const onCloseBindDrawer = () => {
        setBindDrawerOpen(false);
    };
    
    const [unBindDrawerOpen, setUnBindDrawerOpen] = useState(false);
    const showUnBindDrawer = () => {
        setUnBindDrawerOpen(true);
    };
    const onCloseUnBindDrawer = () => {
        setUnBindDrawerOpen(false);
    };

    // 刷新列表
    const handleRefreshList = () => {
        refetch(buildQueryVariables());
        refetchOperatorOptions();
    };

    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
            {/* 筛选条件 */}
            <PricingSchemeApplyFilter
                filters={tempFilters}
                setFilters={setTempFilters}
                handleFilter={handleFilter}
                handleResetFilters={handleResetFilters}
                showBindDrawer={showBindDrawer}
                showUnBindDrawer={showUnBindDrawer}
                operatorOptions={operatorOptions}
                operatorOptionsLoading={operatorOptionsLoading}
            />

            {/* 表格列表 */}
            <PricingSchemeApplyColumns
                rowData={pricingSchemeApplyData}
                loading={loading}
            />

            {/* 分页 */}
            <TablePaginationBar
                total={totalElements}
                current={currentPage}
                pageSize={pageSize}
                onChange={handlePageChange}
            />

            <Drawer
                title="方案绑定/Apply Price"
                width={800}
                onClose={onCloseBindDrawer}
                open={bindDrawerOpen}
                destroyOnClose={true}
                keyboard={false}
                mask={true}
                maskClosable={false}
                styles={{
                body: {
                    paddingBottom: 80,
                },
                }}
            >
                {bindDrawerOpen && (
                    <ApplySchemeComp 
                        businessScenario={businessScenario}
                        isBindView={true}
                        onClose={onCloseBindDrawer}
                        onSuccess={handleRefreshList}
                    />
                )}
            </Drawer>
            <Drawer
                title="方案解绑"
                width={800}
                onClose={onCloseUnBindDrawer}
                open={unBindDrawerOpen}
                destroyOnClose={true}
                keyboard={false}
                mask={true}
                maskClosable={false}
                styles={{
                body: {
                    paddingBottom: 80,
                },
                }}
            >
                {unBindDrawerOpen && (
                    <ApplySchemeComp 
                        businessScenario={businessScenario}
                        isBindView={false}
                        onClose={onCloseUnBindDrawer}
                        onSuccess={handleRefreshList}
                    />
                )}
            </Drawer>
        </div>
    );
};

export default PricingSchemeApplyPage;

